import { Field, FieldArray, Formik, FormikHelpers as FormikActions, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { Button } from "~/components/Button";
import {
  Form,
  FormColumn,
  FormControls,
  FormRow,
  RadioField,
  hasErrors,
  InputField,
  IntentText,
  SelectField,
  ButtonField,
  FormTitle,
  LabelWithLink,
  CheckboxField,
} from "~/components/Form";
import { IModalComponent, IModalComponentClose } from "~/components/Modal";
import { InputType } from "~/components/Controls/Input";
import { TabsWithBody } from "~/components/Tabs";
import { Intent } from "~/lib/intent";
import { useResource } from "~/lib/loader";
import {
  agencyPaymentTypes,
  AgencyNetwork,
  agencyNetworkLabels,
  AgencySchema,
  dspCountingTypes,
  reportingTimezoneOptions,
  IAgency,
  IAdSystemDomain,
  onSubmitPreHandle,
  isP2pAgency,
  REPORTING_API_URL_HELP,
} from "~/modules/Clients/models/Agency";
import { accountInfoConditions } from "~/modules/Clients/models/AccountInfo";
import { fetchDataCubesAgencyForId } from "../api";
import { SelectAccountManagerAgencyField } from "../components/SelectAccountManagerAgency";
import { MultiSelectDataCubesAgencyField } from "../components/SelectDataCubeAgency";
import { SelectPresetField } from "../components/SelectPreset";
import { SelectDspConnectionTypeField } from "../components/SelectDspConnectionType";
import style from "~/modules/Clients/forms/index.module.scss";
import { useConfirm } from '~/components/Dialogs';
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import { dictionaryLinks } from "~/modules/Dictionary/links";

const isP2pPaasFeeEmpty = (fee: number | null | undefined): boolean =>
  fee == null ||
  String(fee).trim() === "" ||
  Number.isNaN(Number(fee));

const P2pPaasFeeDefaulter = () => {
  const { values, setFieldValue } = useFormikContext<IAgency>();
  const enabled = !!values.attributes?.p2pPaas;
  const fee = values.p2pPaasFee;
  useEffect(() => {
    if (!enabled && fee != null) {
      setFieldValue("p2pPaasFee", null);
    }
  }, [enabled, fee, setFieldValue]);
  return null;
};

/**
 * A new agency whose connection type was picked and then cleared keeps no hidden P2P settings: the DSP block does not
 * show them, so nobody could see or undo them.
 */
const withDspDefaults = (agency: IAgency): IAgency =>
  agency.id || isP2pAgency(agency.dspConnectionType)
    ? agency
    : {
        ...agency,
        p2pCoefficient: undefined,
        p2pPaasFee: null,
        attributes: agency.attributes && { ...agency.attributes, p2pPaas: false, p2pReversedPayment: false },
      };

interface IAgencyForm {
  agency?: IAgency;
  onSubmit?: (
    values: IAgency,
    formikActions: FormikActions<IAgency>,
    close: IModalComponentClose
  ) => void;
}

export const AgencyForm: IModalComponent<IAgencyForm> = (props) => {

  const confirm = useConfirm();
  const { resource: dataCubes } = useResource(
    () => (props.agency?.id ? fetchDataCubesAgencyForId(props.agency.id) : Promise.resolve([])),
    []
  );

  const initialAdSystemDomains =
    props.agency?.adSystemDomains?.length
      ? props.agency.adSystemDomains
      : [{ id: undefined, domain: "", tagId: null, default: true }];

  const initialDefaultDomainIndex = String(
    initialAdSystemDomains.findIndex((d) => d.default) ?? 0
  );
  return (
    <Formik
      initialValues={{
        ...props.agency!,
        adSystemDomains: initialAdSystemDomains,
        defaultDomainIndex: initialDefaultDomainIndex,
        dataCubes,
      }}
      enableReinitialize
      validationSchema={AgencySchema}
      onSubmit={async (values, actions) => {
        try {
          const prepared: IAgency = {
            ...onSubmitPreHandle(values),
            adSystemDomains: values.adSystemDomains.map((d, i) => ({
              ...d,
              default: i === Number(values.defaultDomainIndex),
            })),
            p2pPaasFee: values.attributes?.p2pPaas
              ? (isP2pPaasFeeEmpty(values.p2pPaasFee)
                  ? null
                  : Number(values.p2pPaasFee))
              : null,
            attributes: values.attributes ? {
              ...values.attributes,
              accessToken: values.attributes.accessToken || null,
              accessPassword: values.attributes.accessPassword || null,
              publisherId: values.attributes.publisherId || null,
            } : values.attributes,
          };
          await props.onSubmit(withDspDefaults(prepared), actions, props.close);
        } catch (e: any) {
          actions.setStatus(e.response?.data?.message || e.message || "Error saving Agency");
          actions.setSubmitting(false);
        }
      }}
    >
      {(formik) => {
        const { values: agency, status, isSubmitting } = formik;

        return (
          <Form>
            <FormTitle>{agency.id ? "Edit Agency" : "Create Agency"}</FormTitle>
            {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
            <TabsWithBody
                tabs={[
                {
                  title: "General",
                  intent: hasErrors(formik, [
                    "title",
                    "contactName",
                    "instantMessaging",
                    "phone",
                    "email",
                    "paymentType",
                    "netTerms",
                    "dspCountingType",
                  ])
                    ? Intent.DANGER
                    : undefined,
                  children: (
                    <FormRow noBorder>
                      <FormColumn>
                        <Field
                          name="title"
                          label="Title"
                          options={{
                            placeholder: "Title",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="contactName"
                          label="Contact"
                          options={{
                            placeholder: "Contact Name",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="accountManagerId"
                          label={
                            <LabelWithLink
                              label="Account Manager"
                              to={dictionaryLinks.category("agency-managers")}
                              title="Open dictionary: agency managers"
                              ariaLabel="Open dictionary: agency managers"
                              target="_self"
                              onClick={props.close}
                            />
                          }
                          options={{
                            placeholder: "Account Manager",
                            disabled: isSubmitting,
                            clearable: true,
                            searchable: false,
                          }}
                          component={SelectAccountManagerAgencyField}
                        />
                        <Field
                          name="instantMessaging"
                          label="Instant Messaging"
                          options={{
                            placeholder: "Instant Messaging",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                      </FormColumn>
                      <FormColumn>
                        <Field
                          name="phone"
                          label="Phone"
                          options={{
                            placeholder: "Phone",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="email"
                          label="Email"
                          options={{
                            placeholder: "Email",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="dataCubes"
                          label="Data Cubes"
                          options={{
                            placeholder: "Data Cubes",
                            disabled: isSubmitting,
                            clearable: true,
                            searchable: true,
                          }}
                          component={MultiSelectDataCubesAgencyField}
                        />
                        <Field
                          name="presetId"
                          label="Preset"
                          options={{
                            side: "demand",
                            placeholder: "Preset",
                            disabled: isSubmitting,
                            preselectDefault: true,
                            searchable: true,
                          }}
                          component={SelectPresetField}
                        />
                        <Field
                          name="paymentType"
                          options={{
                            placeholder: "Payment Type",
                            items: agencyPaymentTypes.map((option) => ({
                              label: option,
                              value: option,
                            })),
                            disabled: isSubmitting,
                          }}
                          label="Payment Type"
                          component={SelectField}
                        />
                        <Field
                          name="netTerms"
                          options={{
                            placeholder: "Net Terms",
                            items: accountInfoConditions.map((option) => ({
                              label: option,
                              value: option,
                            })),
                            disabled: isSubmitting,
                          }}
                          label="Net Terms"
                          component={SelectField}
                        />
                        <Field
                          name="dspConnectionType"
                          options={{
                            placeholder: "DSP Connection Type",
                            clearable: true,
                            searchable: true,
                            disabled: isSubmitting || !!agency.id,
                            showRawName: false,
                          }}
                          label={
                            <LabelWithLink
                              label="DSP Connection Type"
                              to={dictionaryLinks.category("dsp-connection-types")}
                              title="Open dictionary: DSP connection types"
                              ariaLabel="Open dictionary: DSP connection types"
                              target="_self"
                              onClick={props.close}
                            />
                          }
                          component={SelectDspConnectionTypeField}
                        />
                        <Field
                          name="dspCountingType"
                          options={{
                            placeholder: "DSP Counting Type",
                            items: dspCountingTypes.map((option) => ({
                              label: option,
                              value: option,
                            })),
                            disabled: isSubmitting,
                          }}
                          label="DSP Counting Type"
                          component={SelectField}
                        />
                        <Field
                          name="network"
                          options={{
                            placeholder: "No network",
                            items: Object.values(AgencyNetwork).map((v) => ({
                              label: agencyNetworkLabels[v],
                              value: v,
                            })),
                            clearable: true,
                            disabled: isSubmitting || !!agency.id,
                          }}
                          label="Network"
                          component={SelectField}
                        />
                        <Field
                          name="reportingTimezone"
                          options={{
                            placeholder: "UTC (default)",
                            items: reportingTimezoneOptions,
                            clearable: true,
                            searchable: true,
                            disabled: isSubmitting,
                          }}
                          label="Reporting Timezone"
                          component={SelectField}
                        />
                      </FormColumn>
                    </FormRow>
                  ),
                },
                {
                  title: isP2pAgency(agency.dspConnectionType) ? "P2P" : "DSP",
                  intent: hasErrors(formik, ["attributes", "p2pCoefficient", "p2pPaasFee"]) ? Intent.DANGER : undefined,
                  children: (
                    <FormRow noBorder>
                      <FormColumn>
                        {isP2pAgency(agency.dspConnectionType) && (
                          <>
                            <Field
                              name="p2pCoefficient"
                              options={{
                                placeholder: "P2P Coefficient",
                                disabled: isSubmitting,
                              }}
                              label="P2P Coefficient"
                              component={InputField}
                            />
                            <Field
                              name="p2pGrossBidPrice"
                              label="P2P Gross Bid Price"
                              component={CheckboxField}
                            />
                            <P2pPaasFeeDefaulter />
                            {/*
                              Immutable once the agency exists, in both directions: the PaaS dimensions derive at
                              query time while the money beside them freezes at ingest, so a flip would restate
                              history. Switching sides means a new agency. Same latch as the two create-only
                              fields above.
                            */}
                            <Field
                              name="attributes.p2pPaas"
                              label="P2P PaaS"
                              options={{
                                disabled: isSubmitting || !!agency.id,
                              }}
                              component={CheckboxField}
                            />
                            <Field
                              name="attributes.p2pReversedPayment"
                              label="Is Reverse Payment"
                              options={{
                                disabled: isSubmitting || !!agency.id,
                              }}
                              component={CheckboxField}
                            />
                            <Field
                              name="p2pPaasFee"
                              label="P2P PaaS Fee %"
                              options={{
                                placeholder: "P2P PaaS Fee %",
                                type: InputType.FLOAT,
                                step: "any",
                                disabled: isSubmitting || !agency.attributes?.p2pPaas,
                              }}
                              component={InputField}
                            />
                          </>
                        )}
                        <Field
                          name="attributes.publisherId"
                          label="Publisher ID"
                          options={{
                            placeholder: "Publisher ID",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                      </FormColumn>
                      <FormColumn>
                        <Field
                          name="attributes.reportingApiUrl"
                          label="Reporting API URL"
                          help={REPORTING_API_URL_HELP}
                          options={{
                            placeholder: "https://",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="attributes.accessUsername"
                          label="Access Username"
                          options={{
                            placeholder: "Username",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="attributes.accessPassword"
                          label="Access Password"
                          options={{
                            placeholder: "Password",
                            type: "password",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                        <Field
                          name="attributes.accessToken"
                          label="Access Token"
                          options={{
                            placeholder: "Token",
                            type: "password",
                            disabled: isSubmitting,
                          }}
                          component={InputField}
                        />
                      </FormColumn>
                    </FormRow>
                  ),
                }, {
                  title: "Ad System Domains",
                  intent: hasErrors(formik, ["adSystemDomains"]) ? Intent.DANGER : undefined,
                  children: (
                    <FieldArray
                      name="adSystemDomains"
                      render={(arrayHelpers) => {

                        return (
                          <div>
                            {agency.adSystemDomains?.map((d, index) => (
                              <FormRow noBorder noMargin key={`${d.id ?? 'new'}-${index}`}>
                                <div className={style.domainsForm}>
                                  <Field
                                    name={`adSystemDomains.${index}.domain`}
                                    label="Domain"
                                    className={style.fieldDomains}
                                    component={InputField}
                                    options={{
                                      placeholder: "example.com",
                                      disabled: isSubmitting,
                                    }}
                                  />
                                  <Field
                                    name="defaultDomainIndex"
                                    component={RadioField}
                                    className={style.defaultRadio}
                                    options={{
                                      optionValue: index.toString(),
                                      label: "Default",
                                      disabled: isSubmitting,
                                    }}
                                  />
                                  {agency.adSystemDomains.length > 1 && (
                                    <Field
                                      name={"delete"}
                                      component={ButtonField}
                                      className={style.fieldBtnDelete}
                                      options={{
                                        onClick: () =>
                                          confirm("Delete domain?", {
                                            onOk: () => {
                                              arrayHelpers.remove(index);

                                              if (formik.values.defaultDomainIndex === index.toString()) {
                                                formik.setFieldValue("defaultDomainIndex", "0");
                                              }

                                              else if (Number(formik.values.defaultDomainIndex) > index) {
                                                formik.setFieldValue(
                                                  "defaultDomainIndex",
                                                  (Number(formik.values.defaultDomainIndex) - 1).toString()
                                                );
                                              }
                                            },
                                            icon: <LucideIcon icon={Trash2} size={20} />,
                                            intent: Intent.DANGER,
                                          }),
                                        icon: <i className="fa fa-trash" />,
                                        intent: Intent.DANGER,
                                      }}
                                    />
                                  )}
                                </div>
                              </FormRow>
                            ))}

                            <Button
                              responsive
                              disabled={isSubmitting}
                              intent={Intent.PRIMARY}
                              leftIcon={<i className="fa fa-plus" />}
                              onClick={() => {
                                arrayHelpers.push({
                                  id: undefined,
                                  domain: "",
                                  tagId: null,
                                  default: false,
                                } as IAdSystemDomain);
                              }}
                            >
                              Add Domain
                            </Button>
                          </div>
                        );
                      }}
                    />
                  ),
                },
              ]}
            />
            <FormControls>
              <Button disabled={isSubmitting} onClick={props.close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                intent={Intent.PRIMARY}
                rightIcon={isSubmitting && <i className="fa fa-spinner fa-spin" />}
              >
                Save
              </Button>
            </FormControls>
          </Form>
        );
      }}
    </Formik>
  );
};
